import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@workspace/ui/components/popover";
import { cn } from "@workspace/ui/lib/utils";

import { AllCurrencies } from "./all-currencies";
import { useAllCurrenciesContent } from "./use-all-currencies-content";
import { useFormatCurrency } from "./use-format-currency";
import { useFormatMoneyValue } from "./use-format-money-value";

interface MoneyProps {
	value?: number | null;
	currency?: string | null;
	className?: string;
	options?: Intl.NumberFormatOptions;
	popover?: boolean;
}

export function MoneyText({
	value = 0,
	currency: c = "USD",
	options = {},
}: Pick<MoneyProps, "value" | "currency" | "options">) {
	const currency = c || "USD";
	const formatter = useFormatCurrency(currency, options);
	const valueFormatter = useFormatMoneyValue(currency);

	const majorUnitValue = valueFormatter.to(value);

	return majorUnitValue !== null
		? formatter.format(majorUnitValue)
		: majorUnitValue;
}

export function Money({
	value = 0,
	currency: c = "USD",
	className = "",
	options = {},
	popover = true,
}: MoneyProps) {
	const currency = c || "USD";
	const { currencyRates } = useAllCurrenciesContent({ value });

	if (!popover || value === 0 || currencyRates.length === 0) {
		return <MoneyText value={value} currency={currency} options={options} />;
	}

	return (
		<>
			<Popover>
				<PopoverTrigger
					nativeButton={false}
					render={
						<span
							className={cn(className)}
							title="Click for more details"
							aria-label="Click for more details"
							role="button"
						>
							<MoneyText value={value} currency={currency} options={options} />
						</span>
					}
				/>
				<PopoverContent className="w-48 text-muted-foreground space-y-1">
					<p className="text-sm ">
						{currency ? `Currency: ${currency}` : "No currency specified"}
					</p>
					<div className="text-sm ">
						<AllCurrencies value={value} currency={currency} />
					</div>
				</PopoverContent>
			</Popover>
		</>
	);
}

export { MoneyInput, GroupedMoneyInput } from "./money-input";
